import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { api } from '../App';

// Default modules state (all enabled)
const DEFAULT_MODULES = {
  reservations: true,
  pos: true,
  payments: true,
  charges: true,
  expenses: true,
  hr: true,
  payouts: true,
  reports: true,
  precheckin: true,
  refunds: true,
  documents: true,
  guests: true,
  calendar: true,
  ical: true,
  backup: true
};

// Context for modules
const ModulesContext = createContext(DEFAULT_MODULES);

// Provider component
export function ModulesProvider({ children }) {
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [loading, setLoading] = useState(true);

  const fetchModules = useCallback(async () => {
    // Only fetch modules if user is logged in (has token)
    const token = localStorage.getItem('token');
    if (!token) {
      setModules(DEFAULT_MODULES);
      setLoading(false);
      return;
    }
    
    try {
      const res = await api.get('/config/modules');
      setModules({ ...DEFAULT_MODULES, ...res.data });
    } catch (error) {
      console.error('Error fetching modules:', error);
      // Keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
    
    // Listen for storage events (token changes from login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        fetchModules();
      }
    };
    
    // Listen for custom riad switch event
    const handleRiadSwitch = () => {
      fetchModules();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('riad-switched', handleRiadSwitch);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('riad-switched', handleRiadSwitch);
    };
  }, [fetchModules]);

  const refreshModules = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await api.get('/config/modules');
      setModules({ ...DEFAULT_MODULES, ...res.data });
    } catch (error) {
      console.error('Error refreshing modules:', error);
    }
  };

  return (
    <ModulesContext.Provider value={{ modules, loading, refreshModules }}>
      {children}
    </ModulesContext.Provider>
  );
}

// Hook to use modules
export function useModules() {
  const context = useContext(ModulesContext);
  if (!context) {
    return { modules: DEFAULT_MODULES, loading: false, refreshModules: () => {} };
  }
  return context;
}

// Helper to check if a module is enabled
export function isModuleEnabled(modules, moduleKey) {
  return modules[moduleKey] !== false;
}

export default useModules;
